import { FaEnvelope } from "react-icons/fa";
import { FiClock } from "react-icons/fi";
import type { TFunction } from "i18next";
import Spinner from "../../../components/Spinner";
import type { MailchimpStatus } from "../types/dashboard.types";
import { formatCompactDateTime } from "../utils/dashboardDate.utils";
import { healthBadgeClass } from "../utils/dashboardStatus.utils";

interface MailchimpActionTileProps {
  loading: boolean;
  error: string | null;
  neverSynced: boolean;
  status: MailchimpStatus | null;
  locale: string;
  onClick: () => void;
  t: TFunction;
}

const MailchimpActionTile = ({ loading, error, neverSynced, status, locale, onClick, t }: MailchimpActionTileProps) => {
  const lastSync = status?.lastSyncAt ?? status?.last_sync_at ?? status?.updatedAt ?? status?.updated_at ?? null;
  const configured = status?.configured ?? status?.enabled ?? null;
  const health = loading ? "warning" : configured === false ? "disabled" : error ? "error" : "online";
  const label = neverSynced ? t("dashboard.mailchimp.never_synced", { defaultValue: "Never synced" }) : formatCompactDateTime(lastSync, locale);

  return (
    <div className="rounded-[24px] border border-slate-200/80 bg-white/95 p-5 shadow-[0_12px_30px_rgba(15,23,42,0.08)] dark:border-white/10 dark:bg-slate-950/60">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-stretch xl:justify-between">
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
            <FaEnvelope className="text-2xl text-emerald-600" />
            <span>{t("dashboard.mailchimp.label", { defaultValue: "Mailchimp" })}</span>
          </p>
          {loading && status == null && !neverSynced ? (
            <div className="mt-2 flex items-center gap-2 text-sm text-slate-500 dark:text-slate-300">
              <Spinner />
              {t("common.loading", { defaultValue: "Loading..." })}
            </div>
          ) : error ? (
            <p className="mt-2 text-sm text-red-600 dark:text-red-300">{error}</p>
          ) : (
            <>
              <p className="mt-2 text-3xl font-semibold text-slate-900 dark:text-white">{configured === false ? t("status.disabled", { defaultValue: "Disabled" }) : t("status.online", { defaultValue: "Online" })}</p>
              <p className="mt-3 text-sm text-slate-700 dark:text-slate-200">{label}</p>
            </>
          )}
        </div>
        <div className="flex min-w-0 flex-col items-end justify-between gap-3 xl:min-w-[140px]">
          <div className="ml-auto w-full text-right text-xs font-semibold text-slate-500 dark:text-slate-300">
            <FiClock className="mr-1 inline" />
            {label}
          </div>
          <button
            type="button"
            onClick={onClick}
            disabled={loading}
            className="inline-flex max-w-full shrink items-center justify-center rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-emerald-500/25 transition hover:-translate-y-0.5 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-400 motion-reduce:transform-none self-stretch xl:self-auto"
          >
            {t("common.open", { defaultValue: "Open" })}
          </button>
          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${healthBadgeClass(health)}`}>
            {health === "online"
              ? t("status.online", { defaultValue: "Online" })
              : health === "warning"
                ? t("status.warning", { defaultValue: "Warning" })
                : health === "disabled"
                  ? t("status.disabled", { defaultValue: "Disabled" })
                  : t("status.error", { defaultValue: "Error" })}
          </span>
        </div>
      </div>
    </div>
  );
};

export default MailchimpActionTile;
